import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { serverFetch } from "@server";
import {
  Button,
  Checkbox,
  Input,
  message as antMessage,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FlagOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Dialog from "./dialog";
import { dictsChannelVidName } from "@extensions/dicts";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import dialogSignalRService from "@services/dialogSignalRService";
import "./dialogs.css";

dayjs.extend(isoWeek);

const MESSAGE_DIRECTION_INCOMING = 1;
const MESSAGE_DIRECTION_OUTCOMING = -1;

const FILTER_KEYS = {
  ALL: "all",
  AWAITING: "awaiting",
  MINE: "mine",
  TODAY: "today",
  UNREAD: "isNew",
};

/** Короткие дни недели ISO (пн…вс), неделя с понедельника */
const RU_ISO_WEEKDAY_SHORT = {
  1: "пн",
  2: "вт",
  3: "ср",
  4: "чт",
  5: "пт",
  6: "сб",
  7: "вс",
};

/** Сегодня — только время; эта ISO-неделя — день + время; иначе дата и время */
const formatRelativeLastMessageTime = (value) => {
  if (value == null || value === "") return "";
  const m = dayjs(value);
  if (!m.isValid()) return "";
  const now = dayjs();
  if (m.isSame(now, "day")) return m.format("HH:mm");
  if (m.isSame(now, "isoWeek")) {
    const dow = RU_ISO_WEEKDAY_SHORT[m.isoWeekday()] ?? "";
    return dow ? `${dow}. ${m.format("HH:mm")}` : m.format("HH:mm");
  }
  return m.format("DD.MM.YYYY HH:mm");
};

const directionMeta = (dir) => {
  const n = Number(dir);
  if (n === MESSAGE_DIRECTION_INCOMING)
    return {
      icon: <ArrowLeftOutlined aria-hidden />,
      title: "Входящее",
    };
  if (n === MESSAGE_DIRECTION_OUTCOMING)
    return {
      icon: <ArrowRightOutlined aria-hidden />,
      title: "Исходящее",
    };
  return { icon: null, title: "" };
};

const unwrapDialogPayload = (data) => {
  if (data?.dialog != null) {
    const tags = Array.isArray(data.tags) ? data.tags : [];
    return { ...data.dialog, tags };
  }
  return data;
};

/** HEX для тегов (#…), как в карточке диалога */
const tagHexColor = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return s.startsWith("#") ? s : `#${s}`;
};

const uniqueTagsById = (tags) =>
  [...new Map((Array.isArray(tags) ? tags : []).map((t) => [t.id, t])).values()];

const IndicatorIcon = ({ icon: Icon, className, color }) => (
  <Icon className={className} style={color ? { color } : undefined} />
);

/** Нет менеджера: userId не задан / пустой / неположительный */
const isDialogWithoutManager = (d) => {
  const v = d?.userId;
  if (v == null || v === "") return true;
  const n = Number(v);
  return Number.isFinite(n) && n <= 0;
};

/** Нет клиента: clientId не задан / пустой / неположительный */
const isDialogWithoutClient = (d) => {
  const v = d?.clientId;
  if (v == null || v === "") return true;
  const n = Number(v);
  return Number.isFinite(n) && n <= 0;
};

const isDialogWithClient = (d) => !isDialogWithoutClient(d);

/** Непрочитан: lastMessageIdIsRead не задан или меньше lastMessageId */
const isDialogUnread = (d) => {
  const readId = d?.lastMessageIdIsRead;
  const lastId = d?.lastMessageId;
  if (readId == null) return true;
  if (lastId == null) return false;
  return Number(readId) < Number(lastId);
};

/** Две строки колонки «Контакт»: заголовок и мета (телефон/email и при необходимости дублирующее имя) */
const formatDialogContactCell = (d) => {
  const t = (v) => (v == null ? "" : String(v).trim());
  const hasName = Boolean(t(d.name));
  const withClient = isDialogWithClient(d);

  let line1;
  const line2Parts = [];

  if (withClient) {
    line1 = hasName ? t(d.name) : t(d.clientName);
    if (hasName && t(d.clientName)) line2Parts.push(t(d.clientName));
    if (t(d.clientPhone)) line2Parts.push(t(d.clientPhone));
    if (t(d.clientEmail)) line2Parts.push(t(d.clientEmail));
  } else {
    line1 = hasName ? t(d.name) : t(d.enteredName);
    if (hasName && t(d.enteredName)) line2Parts.push(t(d.enteredName));
    if (t(d.enteredPhone)) line2Parts.push(t(d.enteredPhone));
    if (t(d.enteredEmail)) line2Parts.push(t(d.enteredEmail));
  }

  const line2 = line2Parts.length > 0 ? line2Parts.join(" · ") : "";
  const title = line1 || "";
  return { title, meta: line2 };
};

const Dialogs = () => {
  const [dialogsRaw, setDialogsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalDialogId, setModalDialogId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [signalRConnected, setSignalRConnected] = useState(false);
  const [usersMap, setUsersMap] = useState({});
  const [filterTab, setFilterTab] = useState(FILTER_KEYS.ALL);
  const [searchText, setSearchText] = useState("");
  const [managerFilter, setManagerFilter] = useState(null);
  const [channelFilter, setChannelFilter] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const messagesEndRef = useRef(null);
  const modalDialogIdRef = useRef(null);
  const dialogsRawRef = useRef([]);
  const user = useSelector((s) => s.user);

  modalDialogIdRef.current = modalDialogId;
  dialogsRawRef.current = dialogsRaw;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const getDialogs = useCallback(() => {
    if (!user?.companyId) return;
    setLoading(true);
    const queryParams = {
      companyId: user.companyId,
      begPer: dayjs().subtract(5, "year").startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      endPer: dayjs().add(1, "day").endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    };
    if (!showDeleted) {
      queryParams.deleted = false;
    }
    serverFetch("Message/Dialog", {
      queryParams,
    })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setDialogsRaw(list);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        antMessage.error(e.userMessage);
      });
  }, [user?.companyId, showDeleted]);

  useEffect(() => {
    if (!user?.companyId) return;
    serverFetch("user")
      .then((list) => {
        const m = {};
        (Array.isArray(list) ? list : []).forEach((u) => {
          m[u.id] = u.name ?? u.shortName ?? `#${u.id}`;
        });
        setUsersMap(m);
      })
      .catch(() => {});
  }, [user?.companyId]);

  const getMessages = useCallback(
    (dialogId) => {
      if (!dialogId) return;
      setMessagesLoading(true);
      serverFetch("Message/Message", { queryParams: { dialogId } })
        .then((data) => {
          setMessages(Array.isArray(data) ? data : []);
          setMessagesLoading(false);
          setTimeout(scrollToBottom, 50);
        })
        .catch((e) => {
          setMessagesLoading(false);
          antMessage.error(e.userMessage);
        });
    },
    [scrollToBottom]
  );

  useEffect(getDialogs, [getDialogs]);

  useEffect(() => {
    if (!modalDialogId) {
      setMessages([]);
      return;
    }
    getMessages(modalDialogId);
  }, [modalDialogId, getMessages]);

  useEffect(() => {
    const sessionId = user?.sessionId ?? localStorage.getItem("sessionId");
    if (!user?.companyId || !sessionId) return;

    const onReceiveMessage = (msg) => {
      const dialogId = msg.dialogId;
      if (!dialogId) return;

      const inTable = dialogsRawRef.current.some((d) => d.id === dialogId);
      if (!inTable) {
        getDialogs();
        return;
      }

      serverFetch(`Message/Dialog/${dialogId}`)
        .then((payload) => {
          const dialog = unwrapDialogPayload(payload);
          setDialogsRaw((prev) => {
            const idx = prev.findIndex((d) => d.id === dialogId);
            if (idx < 0) {
              getDialogs();
              return prev;
            }
            const next = [...prev];
            next[idx] = dialog;
            return next;
          });
          if (modalDialogIdRef.current === dialogId) {
            setMessages((prev) => {
              const newMsg = { ...msg, createdOn: msg.createdOn || new Date().toISOString() };
              const idx = prev.findIndex((m) => m.id === msg.id);
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], ...newMsg };
                return next;
              }
              return [...prev, newMsg];
            });
            setTimeout(scrollToBottom, 50);
          }
        })
        .catch(() => {});
    };

    dialogSignalRService
      .joinCompany(sessionId, {
        onReceiveMessage,
        onError: (err) => console.warn("Dialog SignalR:", err?.message),
        onConnectionStateChange: setSignalRConnected,
      })
      .catch(() => setSignalRConnected(false));

    return () => {
      setSignalRConnected(false);
      dialogSignalRService.stop();
    };
  }, [user?.companyId, user?.sessionId, scrollToBottom, getDialogs]);

  const channelOptions = useMemo(() => {
    const map = new Map();
    dialogsRaw.forEach((d) => {
      if (d.channelId != null) map.set(d.channelId, d.channelName || `#${d.channelId}`);
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [dialogsRaw]);

  const managerSelectOptions = useMemo(() => {
    const ids = new Set();
    dialogsRaw.forEach((d) => {
      if (!isDialogWithoutManager(d)) ids.add(d.userId);
    });
    return [...ids].sort((a, b) => (usersMap[a] || "").localeCompare(usersMap[b] || "", "ru")).map((id) => ({
      value: id,
      label: usersMap[id] ?? `#${id}`,
    }));
  }, [dialogsRaw, usersMap]);

  const filteredDialogs = useMemo(() => {
    let list = [...dialogsRaw];

    const q = searchText.trim().toLowerCase();
    if (q) {
      list = list.filter((d) => {
        const blob = [
          d.name,
          d.enteredName,
          d.enteredPhone,
          d.enteredEmail,
          d.clientName,
          d.clientPhone,
          d.clientEmail,
          d.note,
          d.channelName,
          d.lastMessageText,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      });
    }

    if (managerFilter != null) {
      list = list.filter((d) => d.userId === managerFilter);
    }

    if (channelFilter != null) {
      list = list.filter((d) => d.channelId === channelFilter);
    }

    switch (filterTab) {
      case FILTER_KEYS.AWAITING:
        list = list.filter((d) => isDialogWithoutManager(d));
        break;
      case FILTER_KEYS.MINE:
        list = list.filter((d) => d.userId === user?.id);
        break;
      case FILTER_KEYS.TODAY:
        list = list.filter((d) => dayjs(d.lastUpdated).isSame(dayjs(), "day"));
        break;
      case FILTER_KEYS.UNREAD:
        list = list.filter((d) => isDialogUnread(d));
        break;
      default:
        break;
    }

    list.sort((a, b) => dayjs(b.lastUpdated).valueOf() - dayjs(a.lastUpdated).valueOf());

    return list;
  }, [
    dialogsRaw,
    searchText,
    managerFilter,
    channelFilter,
    filterTab,
    user?.id,
  ]);

  const openDialogModal = (dialog) => {
    setSelectedRowKeys([dialog.id]);
    setModalDialogId(dialog.id);
  };

  const handleModalClose = useCallback(() => {
    setModalDialogId(null);
    setSelectedRowKeys([]);
    setMessages([]);
    getDialogs();
  }, [getDialogs]);

  const resetFilters = () => {
    setFilterTab(FILTER_KEYS.ALL);
    setSearchText("");
    setManagerFilter(null);
    setChannelFilter(null);
    setShowDeleted(false);
    setSelectedRowKeys([]);
  };

  const filterTabBtn = (key, icon, label) => (
    <button
      type="button"
      className={`dialogs-filter-tab ${filterTab === key ? "dialogs-filter-tab-active" : ""}`}
      onClick={() => setFilterTab(key)}
    >
      {icon}
      <span>{label}</span>
    </button>
  );

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 72,
      render: (id) => (id != null ? id : "—"),
    },
    {
      title: "Индикаторы",
      key: "flags",
      width: 268,
      render: (_, d) => {
        const tagsList = uniqueTagsById(d.tags);
        return (
          <Space wrap size={[4, 6]} className="dialogs-indicators-cell">
            {isDialogUnread(d) && (
              <Tooltip title="Непрочитано">
                <span>
                  <MailOutlined className="dialogs-icon-unread" style={{ color: "#FFBB3B" }} />
                </span>
              </Tooltip>
            )}
            {d.deleted && (
              <Tooltip title="Удалён">
                <span>
                  <IndicatorIcon icon={CloseCircleOutlined} className="dialogs-icon-deleted" color="#FF4040" />
                </span>
              </Tooltip>
            )}
            {isDialogWithoutManager(d) && (
              <Tooltip title="Лид без менеджера">
                <span>
                  <IndicatorIcon icon={ClockCircleOutlined} className="dialogs-icon-awaiting" color="#FFBB3B" />
                </span>
              </Tooltip>
            )}
            {isDialogWithoutClient(d) && (
              <Tooltip title="Лид без клиента">
                <span>
                  <IndicatorIcon icon={TeamOutlined} className="dialogs-icon-lead" color="#FF4040" />
                </span>
              </Tooltip>
            )}
            {(d.countDialog ?? 0) > 1 && (
              <Tooltip title="Несколько диалогов у клиента">
                <span>
                  <IndicatorIcon icon={FlagOutlined} className="dialogs-icon-multi" color="#32CD32" />
                </span>
              </Tooltip>
            )}
            {/* {d.vid === 101 && (
              <Tooltip title="Телефон">
                <PhoneOutlined className="dialogs-icon-phone" />
              </Tooltip>
            )} */}
            {tagsList.map((t) => {
              const hex = tagHexColor(t.color);
              const label = t.value ?? "";
              return (
                <Tooltip key={`${t.id}-${t.objectVid ?? ""}`} title={label || "Тег"}>
                  <span>
                    <Tag
                      className="dialogs-indicator-tag"
                      style={{
                        marginInlineEnd: 0,
                        ...(hex
                          ? {
                              backgroundColor: hex,
                              borderColor: hex,
                              color: "#fff",
                            }
                          : {}),
                      }}
                    >
                      {label}
                    </Tag>
                  </span>
                </Tooltip>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: "Контакт",
      key: "contact",
      width: 280,
      ellipsis: true,
      render: (_, d) => {
        const { title, meta } = formatDialogContactCell(d);
        return (
          <div className="dialogs-contact-cell">
            <div className="dialogs-contact-name">{title}</div>
            <div className="dialogs-contact-meta">{meta}</div>
          </div>
        );
      },
    },
    {
      title: "Менеджер",
      dataIndex: "userId",
      width: 140,
      ellipsis: true,
      render: (uid) => (uid != null ? usersMap[uid] ?? `#${uid}` : ""),
    },
    {
      title: "Канал",
      key: "source",
      width: 120,
      ellipsis: true,
      render: (_, d) => d.channelName || dictsChannelVidName(d.vid) || "",
    },
    {
      title: "Сообщение",
      key: "message",
      width: 280,
      ellipsis: true,
      render: (_, d) => {
        const ts = d.lastMessageCreatedOn ?? d.lastUpdated;
        const timeStr = formatRelativeLastMessageTime(ts);
        const { icon, title: dirTitle } = directionMeta(d.lastMessageDirection);
        const text = (d.lastMessageText && String(d.lastMessageText).trim()) || "";
        return (
          <div className="dialogs-msg-preview">
            <div className="dialogs-msg-preview-text" title={text !== "" ? text : undefined}>
              {text}
            </div>
            <div className="dialogs-msg-preview-meta">
              {icon ? (
                <Tooltip title={dirTitle}>
                  <span className="dialogs-msg-dir-icon">{icon}</span>
                </Tooltip>
              ) : (
                <span className="dialogs-msg-dir-placeholder" />
              )}
              <span className="dialogs-msg-preview-time">{timeStr || ""}</span>
            </div>
          </div>
        );
      },
    },
  ];

  const rowSelection = {
    type: "radio",
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
      const id = keys[0];
      if (id != null) setModalDialogId(id);
    },
    renderCell: (checked) => <div className={checked ? "list-indicator" : ""} />,
    columnWidth: 40,
  };

  return (
    <div className="dialogs-page-v2">
      <header className="dialogs-page-header">
        <div className="dialogs-page-title-row">
          <div className="dialogs-page-title-with-indicator">
            <Tooltip title={signalRConnected ? "SignalR подключён" : "Нет подключения SignalR"}>
              <span
                className="dialogs-signalr-indicator"
                style={{ backgroundColor: signalRConnected ? "#52c41a" : "#d9d9d9" }}
              />
            </Tooltip>
            <h1 className="dialogs-page-title">Диалоги</h1>
          </div>
        </div>

        <div className="dialogs-filter-tabs-row">
          <div className="dialogs-filter-tabs">
            {filterTabBtn(FILTER_KEYS.UNREAD, <MailOutlined />, "Непрочитанные")}
            {filterTabBtn(FILTER_KEYS.AWAITING, <ClockCircleOutlined />, "Ожидают ответа")}
            {filterTabBtn(FILTER_KEYS.MINE, <UserOutlined />, "Мои обращения")}
            {filterTabBtn(FILTER_KEYS.TODAY, <CalendarOutlined />, "Сегодня активны")}
          </div>
          <Button icon={<ReloadOutlined />} onClick={resetFilters}>
            Сбросить
          </Button>
        </div>

        <div className="dialogs-search-bar">
          <Input
            allowClear
            prefix={<SearchOutlined className="dialogs-search-icon" />}
            placeholder="Поиск по имени, телефону, email, примечанию"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            allowClear
            placeholder="Все менеджеры"
            style={{ minWidth: 160 }}
            options={managerSelectOptions}
            value={managerFilter ?? undefined}
            onChange={setManagerFilter}
          />
          <Select
            allowClear
            placeholder="Все каналы"
            style={{ minWidth: 160 }}
            options={channelOptions}
            value={channelFilter ?? undefined}
            onChange={setChannelFilter}
          />
          <Checkbox checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}>
            Показывать удалённые
          </Checkbox>
          <Tooltip title="Обновить">
            <Button
              type="default"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => getDialogs()}
              aria-label="Обновить список обращений"
              className="dialogs-search-refresh-btn"
            />
          </Tooltip>
        </div>
      </header>

      <div className="dialogs-page-table-wrap">
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            size="middle"
            className="dialogs-table"
            columns={columns}
            dataSource={filteredDialogs}
            pagination={false}
            rowSelection={rowSelection}
            tableLayout="fixed"
            onRow={(record) => ({
              onClick: (e) => {
                if (
                  e.target.closest?.(
                    "button,a,.ant-checkbox-wrapper,.ant-checkbox,.ant-radio-wrapper,.ant-radio"
                  )
                )
                  return;
                openDialogModal(record);
              },
            })}
          />
        </Spin>
      </div>

      {modalDialogId != null && (
        <Dialog
          id={modalDialogId}
          onClose={handleModalClose}
          messages={messages}
          setMessages={setMessages}
          messagesLoading={messagesLoading}
          messagesEndRef={messagesEndRef}
          scrollToBottom={scrollToBottom}
          onSent={getDialogs}
        />
      )}
    </div>
  );
};

export default Dialogs;
